import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { Meeting } from '@cds/betting-offer/tags';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import {
    GreyhoundParticipantInfo,
    MeetingModel,
    RaceDetailsModel,
    WinGameFixture,
    WinGameFixturePreview,
} from '@frontend/sports/racing/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService, IntlService } from '@frontend/vanilla/core';
import { Subject, takeUntil } from 'rxjs';

import { convertDayOfRaceToUrlParam, getDayOfRace } from '../../../../horse-racing-shared/helpers';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { TrackNavArea } from '../../../../navigation/navigation-tracking.model';
import { SubNavigationItem } from '../../../../sub-navigation/sub-navigation.models';
import { FixtureUpdateService } from '../../../common/fixture-update.service';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';
import { RacingService } from '../../../services/racing-service';

@HooksWireup()
@Component({
    selector: 'ms-greyhounds-race-details',
    templateUrl: './greyhounds-race-details.component.html',
    styleUrl: './greyhounds-race-details.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GreyhoundsRaceDetailsComponent extends OnRouteResolve<RaceDetailsModel> {
    @HostBinding('class') className = 'greyhounds-details-container';
    @Input() iconTemplate: TemplateRef<any>;
    private _fixture: WinGameFixture;
    private modelFixtureChanged = new Subject<void>();

    isEmpty = true;
    meeting: Meeting;
    dayOfRace: DayOfRaceDate;
    tabs: SubNavigationItem[] = [];
    currentTitle: SubNavigationItem;
    title = '';
    countDownTimer: number;
    dayOfRaceDateTomorrow = DayOfRaceDate.Tomorrow;
    runners: number;
    marketTitle = new Map<string, string>();
    isMobile: boolean;
    greyhoundTrapDogMapping: { [id: number]: GreyhoundParticipantInfo } = {};

    constructor(
        public sitecore: Sitecore,
        private changeDetector: ChangeDetectorRef,
        public override route: ActivatedRoute,
        private intlService: IntlService,
        private urlHelper: UrlHelperService,
        private racingService: RacingService,
        private deviceService: DeviceService,
        private redirectHelper: RedirectHelperService,
        private trackingService: TrackingService,
        private greyhoundTrackingService: GreyhoundTrackingService,
        protected fixtureUpdateService: FixtureUpdateService,
        private horsesFixtureParticipantHelperService: HorsesFixtureParticipantHelper,
    ) {
        super(route);
        this.isMobile = this.deviceService.isMobile;
    }

    get fixture(): WinGameFixture {
        return this._fixture;
    }

    set fixture(value: WinGameFixture) {
        this._fixture = this.racingService.filterRaceSummary(value);
        this.isEmpty = false;
        this.title = this.racingService.getMarketName(this.fixture);
        this.runners = this.horsesFixtureParticipantHelperService.getRunnersCount(this._fixture);
        this.setCountDownTimer();
        this.changeDetector.markForCheck();
    }

    get isResulted(): boolean {
        return this.fixture.addons.pricingState === FixturePricingState.Resulted;
    }

    readonly trackingSource = 'Greyhounds_Racecard';

    onRouteResolved(model: RaceDetailsModel): void {
        this.isEmpty = true;
        if (!model || !model.fixture) {
            return;
        }

        if (typeof this.fixture === 'undefined' || this.fixture.meeting.id !== model.fixture.meeting.id) {
            this.meeting = model.fixture.meeting;
            this.modelFixtureChanged.next();
            this.dayOfRace = getDayOfRace(model.fixture.startDate);
        }
        if (model) {
            const raceId = (this.route.snapshot.params.raceId && this.route.snapshot.params.raceId.toString()) || '';
            const all = this.buildAllTab(raceId, model);
            const raceTabs = model.fixtures.map((f) => this.fixtureToTab(f, model));
            this.tabs = [all, ...raceTabs];
        }

        const activeTitle = this.tabs.find((x) => x.active);
        if (activeTitle) {
            this.currentTitle = activeTitle;
        }
        this.marketTitle.set(model.fixture.id, this.racingService.getMarketName(model.fixture) || '');

        this.fixture = model.fixture;
        this.greyhoundTrapDogMapping = this.racingService.getGrehoundTrapDogMapping(
            this.horsesFixtureParticipantHelperService.getParticipants(model.fixture.participants),
        );

        this.fixtureUpdateService
            .handleFixtureUpdates(this.fixture)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.modelFixtureChanged))
            .subscribe((updatedFixture) => {
                if (this.fixture.id === updatedFixture.id) {
                    this.fixture = updatedFixture;
                }
            });
    }

    private buildAllTab(raceId: string, model: MeetingModel): SubNavigationItem {
        return {
            id: 0,
            title: this.sitecore.horseRacing.common.All,
            url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, model.meeting, convertDayOfRaceToUrlParam(model.day)),
            active: !raceId,
            ignoreBrowserHistory: true,
        };
    }

    private setCountDownTimer(): void {
        if (this.dayOfRace !== DayOfRaceDate.Today) {
            return;
        }

        this.countDownTimer = DateProviderService.getTimeDifference(new Date(this.fixture?.startDate), true);
    }

    get active(): SubNavigationItem {
        return (this.tabs && this.tabs.filter((tab) => tab.active).pop()) ?? this.tabs[0];
    }

    selected(activeTabId: string | number): void {
        const tabItem = this.tabs?.find((t) => t.id.toString() === activeTabId.toString());
        if (!tabItem?.url) {
            return;
        }
        this.greyhoundTrackingService.trackRaceDetailMeeting(tabItem.title, this.meeting, tabItem.url, this.fixture);
        this.redirectHelper.goToPage(tabItem.url, tabItem.ignoreBrowserHistory);
    }

    private fixtureToTab(fixture: WinGameFixturePreview, model: RaceDetailsModel): SubNavigationItem {
        const resulted = fixture.addons.pricingState === FixturePricingState.Resulted;

        return {
            id: +fixture.fixtureId,
            title: this.intlService.formatDate(fixture.startDate, 'HH:mm') || '',
            url: this.urlHelper.getRacingUrlByFixture(SportConstant.Greyhounds, fixture),
            active: fixture.id === model.fixture.id,
            icon: resulted ? 'sports-race-resulted' : '',
            classes: resulted ? 'race-resulted' : '',
            ignoreBrowserHistory: true,
        };
    }

    goBack = (): void => {
        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: `${TrackNavArea.NavBreadcrumb}_Back`,
        });
        this.redirectHelper.goBack();
    };
}
